import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';

import { AuthService } from '../../../core/services/auth.service';

type ImportRow = {
  id: number;
  excelRow: number;
  hoTen: string;
  gioiTinh: string;
  ngaySinh: string;
  soTheCCCD: string;
  diaChi: string;
  diaChiThuongTru: string;
  khuPho: string;
  congTacVien: string;
  status: 'ok' | 'error' | 'duplicate';
  errors: string[];
};

@Component({
  selector: 'app-import-excel',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="import-page">

      <header class="page-head">
        <div class="head-left">
          <h1 class="page-title">Nhập Dữ Liệu Từ Excel</h1>
        </div>
        <div class="head-right">
          <button type="button" class="outline-btn" (click)="downloadTemplate()">
            ↓ Tải file mẫu
          </button>
        </div>
      </header>

      @if (!hasFile()) {
        <section
          class="upload-zone"
          [class.dragging]="isDragging()"
          (click)="fileInput.click()"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)">
          <i class="pi pi-cloud-upload upload-icon"></i>
          <div class="upload-title">Kéo thả file Excel vào đây</div>
          <div class="upload-mid">hoặc</div>
          <button type="button" class="outline-btn"
                  (click)="fileInput.click(); $event.stopPropagation()">
            Chọn file từ máy
          </button>
          <div class="upload-note">Chỉ chấp nhận file .xlsx · Tối đa 10MB</div>
        </section>
      }

      @if (hasFile()) {
        <div class="file-bar">
          <div class="file-bar-left">
            <span class="file-chip">{{ fileName() }}</span>
            <span class="file-dot">·</span>
            <span class="row-badge">{{ importedRows().length }} dòng</span>
          </div>
          <button type="button" class="clear-file-btn" (click)="clearFile()">
            ✕ Đổi file
          </button>
        </div>

        <div class="import-summary">
          <span class="summary-ok">✅ {{ okCount() }} hợp lệ</span>
          <span class="summary-sep">·</span>
          <span class="summary-dup">⚠️ {{ dupCount() }} trùng CCCD</span>
          <span class="summary-sep">·</span>
          <span class="summary-err">❌ {{ errorCount() }} lỗi</span>
        </div>

        @if (problemRows().length > 0) {
          <div class="section-card">
            <div class="section-title">Dòng cần xem xét</div>
            <div class="problem-table-wrap">
              <table class="problem-table">
                <thead>
                  <tr>
                    <th>Dòng (Excel)</th>
                    <th>Số CCCD</th>
                    <th>Họ tên</th>
                    <th>Vấn đề</th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of problemRows(); track r.id) {
                    <tr [class.row-error]="r.status === 'error'"
                        [class.row-dup]="r.status === 'duplicate'">
                      <td class="col-row">{{ r.excelRow }}</td>
                      <td class="col-cccd">{{ r.soTheCCCD || '—' }}</td>
                      <td class="col-name">{{ r.hoTen || '—' }}</td>
                      <td class="col-issue">
                        @if (r.status === 'error') {
                          <span class="badge-error">❌ {{ r.errors.join(' · ') }}</span>
                        }
                        @if (r.status === 'duplicate') {
                          <span class="badge-dup">⚠️ {{ r.errors[0] || 'Trùng CCCD' }}</span>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        } @else if (importedRows().length > 0) {
          <div class="no-problem">Không có dòng nào cần xem xét</div>
        }

        <div class="action-bar">
          <button
            type="button"
            class="btn-recheck"
            [class.done]="recheckDone() && !isRechecking()"
            [disabled]="isRechecking() || isSaving()"
            (click)="runRecheck()">
            @if (isRechecking()) { Đang kiểm tra... }
            @else if (recheckDone()) { ✅ Đã kiểm tra }
            @else { Kiểm tra CCCD }
          </button>

          @if (canImport()) {
            <button
              type="button"
              class="btn-import"
              [disabled]="isSaving() || isRechecking()"
              (click)="saveRows()">
              @if (isSaving()) { Đang nhập... }
              @else { Hoàn Tất ({{ okCount() }} dòng) }
            </button>
          }
        </div>

        @if (errorCount() > 0) {
          <div class="error-hint">
            Còn {{ errorCount() }} dòng lỗi. Vui lòng sửa file Excel và upload lại.
          </div>
        }
      }

      @if (importProgress() !== null) {
        <div class="overlay">
          <div class="overlay-box">
            <div class="overlay-title">Đang nhập dữ liệu...</div>
            <div class="overlay-sub">
              Batch {{ importProgress()!.batch }}/{{ importProgress()!.totalBatches }}
              &nbsp;·&nbsp;
              {{ importProgress()!.current }}/{{ importProgress()!.total }} dòng
            </div>
            <div class="progress-track">
              <div class="progress-fill"
                   [style.width.%]="importProgress()!.total > 0
                     ? (importProgress()!.current / importProgress()!.total) * 100
                     : 0">
              </div>
            </div>
          </div>
        </div>
      }

      @if (showSuccessModal()) {
        <div class="overlay">
          <div class="overlay-box">
            <div class="modal-icon">✅</div>
            <div class="overlay-title">Nhập thành công!</div>
            <div class="modal-row">
              <span class="modal-label">Đã nhập:</span>
              <span class="modal-value">{{ successOk() }} dòng</span>
            </div>
            @if (successSkipped() > 0) {
              <div class="modal-row">
                <span class="modal-label">Bỏ qua:</span>
                <span class="modal-value">{{ successSkipped() }} trùng CCCD</span>
              </div>
            }
            <button type="button" class="btn-modal-ok" (click)="confirmSuccess()">
              OK
            </button>
          </div>
        </div>
      }

      @if (showFailModal()) {
        <div class="overlay">
          <div class="overlay-box">
            <div class="modal-icon">⚠️</div>
            <div class="overlay-title">Có gián đoạn</div>
            <div class="modal-msg">{{ failMessage() }}</div>
            <button type="button" class="btn-modal-ok" (click)="confirmFail()">
              Xác nhận
            </button>
          </div>
        </div>
      }

      <input #fileInput class="file-input" type="file" accept=".xlsx,.xls"
             (change)="onFileSelected($event)" />

    </section>
  `,
  styleUrl: './import-excel.component.scss'
})
export class ImportExcelComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly importedRows = signal<ImportRow[]>([]);
  readonly isSaving = signal(false);
  readonly fileName = signal('');
  readonly isDragging = signal(false);
  readonly isRechecking = signal(false);
  readonly recheckDone = signal(false);
  readonly importProgress = signal<{
    current: number; total: number; batch: number; totalBatches: number;
  } | null>(null);
  readonly showSuccessModal = signal(false);
  readonly successOk = signal(0);
  readonly successSkipped = signal(0);
  readonly showFailModal = signal(false);
  readonly failMessage = signal('');

  readonly hasFile = computed(() => this.fileName().length > 0);
  readonly okCount = computed(() =>
    this.importedRows().filter(r => r.status === 'ok').length
  );
  readonly errorCount = computed(() =>
    this.importedRows().filter(r => r.status === 'error').length
  );
  readonly dupCount = computed(() =>
    this.importedRows().filter(r => r.status === 'duplicate').length
  );
  readonly problemRows = computed(() =>
    this.importedRows().filter(r => r.status !== 'ok')
  );
  readonly canImport = computed(() =>
    this.errorCount() === 0 && this.importedRows().length > 0
  );

  async downloadTemplate(): Promise<void> {
    const filename = 'MauNhapLieu_DSBenhNhan.xlsx';
    const candidates = [
      '/assets/mock/MauNhapLieu_DSBenhNhan.xlsx',
      '/api/import/template',
    ];

    for (const url of candidates) {
      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const blob = await response.blob();
        this.triggerDownload(blob, filename);
        return;
      } catch {
        continue;
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.hasFile()) {
      this.isDragging.set(true);
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      void this.parseFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      void this.parseFile(file);
    }
    input.value = '';
  }

  clearFile(): void {
    this.importedRows.set([]);
    this.fileName.set('');
    this.isDragging.set(false);
    this.isSaving.set(false);
    this.recheckDone.set(false);
    this.importProgress.set(null);
    this.showSuccessModal.set(false);
    this.showFailModal.set(false);
    this.failMessage.set('');
    this.successOk.set(0);
    this.successSkipped.set(0);
    this.isRechecking.set(false);
  }

  async saveRows(): Promise<void> {
    if (this.isSaving() || this.isRechecking()) return;

    await this.runRecheck();
    if (this.errorCount() > 0) return;

    const CHUNK_SIZE = 500;
    const nguoitaoId = this.authService.currentUser()?.nhanvien_id ?? null;
    const okRows = this.importedRows().filter(r => r.status === 'ok');

    if (okRows.length === 0) return;

    const chunks: typeof okRows[] = [];
    for (let i = 0; i < okRows.length; i += CHUNK_SIZE) {
      chunks.push(okRows.slice(i, i + CHUNK_SIZE));
    }

    this.isSaving.set(true);
    this.importProgress.set({
      current: 0, total: okRows.length, batch: 0, totalBatches: chunks.length,
    });

    let totalOk = 0;
    let totalSkipped = 0;

    try {
      for (let b = 0; b < chunks.length; b++) {
        this.importProgress.set({
          current: b * CHUNK_SIZE,
          total: okRows.length,
          batch: b + 1,
          totalBatches: chunks.length,
        });

        const payload = chunks[b].map(row => ({
          ten_benhnhan: row.hoTen,
          gioitinh: row.gioiTinh,
          ngaysinh: row.ngaySinh,
          cmnd: row.soTheCCCD,
          diachi: row.diaChi,
          diachithuongtru: row.diaChiThuongTru,
          khupho: row.khuPho,
          nguoigioithieu: row.congTacVien || null,
        }));

        let res: Response;
        try {
          res = await fetch('/api/import/confirm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rows: payload, nguoitao_id: nguoitaoId }),
          });
        } catch {
          const done = b * CHUNK_SIZE;
          this.failMessage.set(
            `Đã nhập ${done.toLocaleString('vi-VN')}/${okRows.length.toLocaleString('vi-VN')} dòng. Có gián đoạn. Liên hệ IT.`
          );
          this.showFailModal.set(true);
          return;
        }

        if (!res.ok) {
          const done = b * CHUNK_SIZE;
          this.failMessage.set(
            `Đã nhập ${done.toLocaleString('vi-VN')}/${okRows.length.toLocaleString('vi-VN')} dòng. Có gián đoạn. Liên hệ IT.`
          );
          this.showFailModal.set(true);
          return;
        }

        const result = await res.json().catch(() => ({}));
        totalOk += result.ok ?? 0;
        totalSkipped += result.skipped ?? 0;
      }

      this.successOk.set(totalOk);
      this.successSkipped.set(totalSkipped);
      this.showSuccessModal.set(true);

    } finally {
      this.isSaving.set(false);
      this.importProgress.set(null);
    }
  }

  private async parseFile(file: File): Promise<void> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];

      if (!ws) {
        this.fileName.set(file.name);
        this.importedRows.set([]);
        return;
      }

      const allRows = XLSX.utils.sheet_to_json(ws, {
        header: 1, range: 0, defval: ''
      }) as unknown[][];

      const { dataStart, colMap } = this.findHeaderAndDataStart(allRows);
      const col = (name: string): number => colMap[name] ?? -1;

      const seenCccd = new Map<string, number>();

      const rawRows: ImportRow[] = [];
      let id = 1;

      for (let i = 0; i < allRows.length - dataStart; i++) {
        const row = allRows[dataStart + i];
        if (!Array.isArray(row) || !row.some(c => this.cellText(c).trim())) continue;

        const excelRow = dataStart + i + 1;

        const hoTen = col('HOTEN') >= 0 ? this.cellText(row[col('HOTEN')]) : '';
        const gioiTinh = col('GIOI') >= 0 ? this.cellText(row[col('GIOI')]) : '';
        const ngaySinh = col('NGAYSINH') >= 0 ? this.formatExcelDate(row[col('NGAYSINH')]) : '';
        const soTheCCCD = col('SOCCCD') >= 0 ? this.cellText(row[col('SOCCCD')]) : '';
        const diaChi = col('DIACHI') >= 0 ? this.cellText(row[col('DIACHI')]) : '';
        const diaChiThuongTru = col('DIACHITHUONGTRU') >= 0 ? this.cellText(row[col('DIACHITHUONGTRU')]) : '';
        const khuPho = col('KHUPHO') >= 0 ? this.cellText(row[col('KHUPHO')]) : '';
        const congTacVien = col('CONGTACVIEN') >= 0 ? this.cellText(row[col('CONGTACVIEN')]) : '';

        const errors = this.validateRow({ hoTen, gioiTinh, ngaySinh, soTheCCCD });

        let status: ImportRow['status'] = errors.length > 0 ? 'error' : 'ok';
        if (status === 'ok' && soTheCCCD.trim()) {
          const cccdKey = soTheCCCD.trim();
          if (seenCccd.has(cccdKey)) {
            status = 'duplicate';
            errors.push(`Trùng CCCD với dòng ${seenCccd.get(cccdKey)}`);
          } else {
            seenCccd.set(cccdKey, excelRow);
          }
        }

        rawRows.push({
          id: id++, excelRow,
          hoTen, gioiTinh, ngaySinh, soTheCCCD,
          diaChi, diaChiThuongTru, khuPho, congTacVien,
          status, errors,
        });
      }

      this.importedRows.set(rawRows);
      this.fileName.set(file.name);
      this.isDragging.set(false);
      this.recheckDone.set(false);
    } catch {
      this.importedRows.set([]);
      this.fileName.set(file.name);
    }
  }

  private validateRow(fields: {
    hoTen: string; gioiTinh: string; ngaySinh: string; soTheCCCD: string;
  }): string[] {
    const errors: string[] = [];
    if (!fields.hoTen.trim()) errors.push('Thiếu họ tên');
    if (!fields.soTheCCCD.trim()) errors.push('Thiếu số CCCD');
    if (!fields.ngaySinh.trim()) {
      errors.push('Thiếu ngày sinh');
    } else {
      const parts = fields.ngaySinh.trim().split('/');
      const valid = parts.length === 3
        && parts.every(p => /^\d+$/.test(p))
        && Number(parts[0]) >= 1 && Number(parts[0]) <= 31
        && Number(parts[1]) >= 1 && Number(parts[1]) <= 12
        && Number(parts[2]) >= 1900;
      if (!valid) errors.push('Ngày sinh không hợp lệ');
    }
    const gt = fields.gioiTinh.trim().toLowerCase();
    if (gt !== 'nam' && gt !== 'nữ' && gt !== 'nu') {
      errors.push('Giới tính không hợp lệ');
    }
    return errors;
  }

  async runRecheck(): Promise<void> {
    if (this.isRechecking()) return;
    this.isRechecking.set(true);
    try {
      const cmnds = this.importedRows()
        .filter(r => r.soTheCCCD.trim() && r.status !== 'error')
        .map(r => r.soTheCCCD.trim());

      if (cmnds.length === 0) {
        this.recheckDone.set(true);
        return;
      }

      const response = await fetch('/api/import/recheck', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cmnds }),
      });
      const result: Record<string, boolean> = await response.json().catch(() => ({}));

      this.importedRows.update(rows => rows.map(row => {
        if (row.status === 'error') return row;
        const isDbDup = !!result[row.soTheCCCD.trim()];
        if (!isDbDup && row.status === 'duplicate' &&
            row.errors.some(e => e.startsWith('Trùng CCCD với dòng'))) {
          return row;
        }
        return {
          ...row,
          status: isDbDup ? 'duplicate' as const : 'ok' as const,
          errors: isDbDup ? ['CCCD đã tồn tại trong hệ thống'] : [],
        };
      }));

      this.recheckDone.set(true);
    } finally {
      this.isRechecking.set(false);
    }
  }

  confirmSuccess(): void {
    this.showSuccessModal.set(false);
    this.router.navigate(['/patients']);
  }

  confirmFail(): void {
    this.showFailModal.set(false);
  }

  private findHeaderAndDataStart(rows: unknown[][]): {
    dataStart: number;
    colMap: Record<string, number>;
  } {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] as unknown[];
      const headerIdx = row.findIndex(
        cell => typeof cell === 'string' && cell.toUpperCase() === 'HOTEN'
      );
      if (headerIdx !== -1) {
        const colMap: Record<string, number> = {};
        for (let c = 0; c < row.length; c++) {
          const cell = row[c];
          if (typeof cell === 'string' && cell.trim()) {
            colMap[cell.trim().toUpperCase()] = c;
          }
        }
        return { dataStart: i + 2, colMap };
      }
    }
    return {
      dataStart: 8,
      colMap: {
        STT: 0, HOTEN: 1, GIOI: 2, NGAYSINH: 3,
        SOCCCD: 4, DIACHI: 5, DIACHITHUONGTRU: 6,
        KHUPHO: 7, CONGTACVIEN: 8,
      },
    };
  }

  private cellText(value: unknown): string {
    if (value === null || value === undefined) return '';
    return typeof value === 'string' ? value : String(value);
  }

  private formatExcelDate(value: unknown): string {
    if (!value) return '';
    if (value instanceof Date) {
      const d = value.getDate().toString().padStart(2, '0');
      const m = (value.getMonth() + 1).toString().padStart(2, '0');
      const y = value.getFullYear();
      return `${d}/${m}/${y}`;
    }
    return String(value).trim();
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}
